/**
 * ============================================================================
 * useSuppliersQuery — Phase 6d frontend (Supplier Management Module).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the procurement-officer / QA workbench
 *          list of suppliers. Server returns a page with the per-supplier
 *          summary plus a `stats` envelope for the four hero KPIs.
 * Endpoint: GET /api/v1/suppliers
 *
 * NOTE on the `payload` field: per-supplier opaque extras (rating bands,
 *  questionnaire counts, vendor portal URL, etc.) come back as a JSON map.
 *  We narrow at the call site rather than weakening the public type to `any`.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type SupplierCriticality = 'CRITICAL' | 'MAJOR' | 'MINOR' | 'LOW';

export type SupplierLifecycleStatus =
  | 'PENDING_REQUEST'
  | 'QUESTIONNAIRE_ISSUED'
  | 'AUDIT_SCHEDULED'
  | 'ACTIVE'
  | 'RESTRICTED'
  | 'DISQUALIFIED'
  | 'ARCHIVED';

export type RiskCategory = 'CRITICAL' | 'MAJOR' | 'MINOR' | 'LOW';

export type SupplierSubtype =
  | 'API'
  | 'EXCIPIENT'
  | 'PACKAGING'
  | 'EQUIPMENT'
  | 'CONTRACT_MFG'
  | 'CONTRACT_LAB'
  | 'CALIBRATION'
  | 'CONSUMABLE'
  | 'IT'
  | 'OTHER';

export interface SupplierSummary {
  supplier_id: string;
  name: string;
  legal_name: string | null;
  criticality: SupplierCriticality;
  subtype: SupplierSubtype;
  lifecycle_status: SupplierLifecycleStatus;
  risk_score: number | null;
  risk_category: RiskCategory | null;
  next_requalification_at_utc: string | null;
  last_assessment_at_utc: string | null;
  site_code: string | null;
  /** Opaque per-supplier extras; narrow before use. */
  payload: Record<string, unknown>;
}

export interface SupplierWorkbenchStats {
  total_active: number;
  critical_risk_count: number;
  awaiting_review: number;
  requalification_due_this_month: number;
}

export interface SuppliersResponse {
  items: SupplierSummary[];
  stats: SupplierWorkbenchStats;
  total: number;
}

export interface SuppliersQueryParams {
  criticality?: SupplierCriticality;
  lifecycle_status?: SupplierLifecycleStatus;
  risk_category?: RiskCategory;
  subtype?: SupplierSubtype;
  q?: string;
}

export function useSuppliersQuery(
  params: SuppliersQueryParams,
): UseQueryResult<SuppliersResponse> {
  return useQuery<SuppliersResponse>({
    queryKey: ['suppliers', 'workbench', params],
    queryFn: ({ signal }) =>
      apiFetch<SuppliersResponse>('/api/v1/suppliers', {
        query: {
          criticality: params.criticality,
          lifecycle_status: params.lifecycle_status,
          risk_category: params.risk_category,
          subtype: params.subtype,
          q: params.q,
        },
        signal,
      }),
  });
}
